'use client';
import { useState } from 'react';
import { useConfig } from 'app/hooks/useConfig';
import { useTranslations } from 'app/hooks/useTranslations';
import AuthenticationService from 'app/services/authentication';
import ConfigService from 'app/services/configService';
import { useSessionStore } from 'app/stores/globalStore';
import { useTranslationStore } from 'app/stores/translationStore';
import useRoutes from 'app/utils/useRoutes';
import Loader from 'components/Loader/loader';
import { useRouter } from 'next/navigation';

import { AuthenticationComponent } from './authentication';

export default function AuthenticationPage() {
  const [username, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotMode, setIsForgotMode] = useState(false);
  const router = useRouter();
  const ROUTES = useRoutes();
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');
  const [errorEmail, setErrorEmail] = useState<string | undefined>('');
  const { setLoginUser } = useSessionStore(state => state);
  const { status } = useConfig();
  const { fetchTranslations, setLang, t } = useTranslations();
  const currentLang = useTranslationStore(s => s.currentLang);
  const authService = new AuthenticationService(
    process.env.NEXT_PUBLIC_API_BASE_URL || ''
  );

  const handleUserNameChange = (event: any) => {
    const userValue = event.target.value.trim();
    setUserName(userValue);
  };
  const handleChangePassword = (event: any) => {
    setPassword(event.target.value);
  };

  const resolveAndLoadTranslations = async () => {
    const configService = new ConfigService(
      process.env.NEXT_PUBLIC_API_BASE_URL || ''
    );
    let availableLangs: string[] = [];
    try {
      availableLangs = await configService.getLanguages();
    } catch {
      availableLangs = [currentLang];
    }

    const preferredLang = availableLangs.includes(currentLang)
      ? currentLang
      : (availableLangs[0] ?? currentLang);

    setLang(preferredLang);
    const loaded = await fetchTranslations(preferredLang);

    if (!loaded) {
      const fallbacks = availableLangs.filter(l => l !== preferredLang);
      for (const fallback of fallbacks) {
        const fallbackLoaded = await fetchTranslations(fallback);
        if (fallbackLoaded) {
          setLang(fallback);
          break;
        }
      }
    }
  };

  const handleLogin = async () => {
    setIsLoading(true);

    await resolveAndLoadTranslations();
    await authService
      .Login(username, password)
      .then(async (data: any) => {
        if (data.agentId != null) {
          setLoginUser(data);
          router.push(ROUTES.menu);
        } else {
          handleErrorMessage(t('error.login'));
        }
      })
      .catch((err: any) => {
        handleErrorMessage(t('error.login') + ' ' + err.message);
      });
  };

  function handleErrorMessage(error: string): void {
    setIsLoading(false);
    setErrorMessage(error);
    setTimeout(() => {
      setErrorMessage(undefined);
    }, 3000);
    setUserName('');
    setPassword('');
  }

  if (status === 'loading') {
    return <Loader />;
  }

  if (status === 'error' || status === 'not-configured') {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <p className="text-sm text-red-600">{t('error.loading')}</p>
      </div>
    );
  }

  return (
    <AuthenticationComponent
      username={username}
      password={password}
      handleChangeUsername={handleUserNameChange}
      handleChangePassword={handleChangePassword}
      isLoading={isLoading}
      handleLogin={handleLogin}
      errorMessage={errorMessage}
      errorEmail={errorEmail}
      isForgotMode={isForgotMode}
      setIsForgotMode={setIsForgotMode}
    />
  );
}
